'use client';

import {
	PropsWithChildren,
	RefObject,
	createContext,
	useCallback,
	useContext,
	useSyncExternalStore,
} from 'react';

const graphMinInlineSize = 630; // This size is the minimum inline size Victory uses to scale down when the graph is smaller

const cbsGraphMeasurementsContext = createContext<
	| {
			graphInlineSize: number;
			graphBlockSize: number;
	  }
	| undefined
>(undefined);

const graphMeasurementsCache = new WeakMap<
	HTMLElement,
	{ graphInlineSize: number; graphBlockSize: number }
>();

const defaultMeasurements = {
	graphInlineSize: 630,
	graphBlockSize: 450,
};

const getGraphMeasurements = (node: HTMLDivElement | null) => {
	if (node == null) return defaultMeasurements;
	const cachedMeasurements = graphMeasurementsCache.get(node);

	if (cachedMeasurements != null) return cachedMeasurements;

	const { width, height } = node.getBoundingClientRect();

	if (width === 0 || height === 0) return defaultMeasurements;

	const ratio = height / width;
	const graphInlineSize = Math.max(width, graphMinInlineSize);
	const graphBlockSize = graphInlineSize * ratio;
	const measurements = { graphInlineSize, graphBlockSize };
	graphMeasurementsCache.set(node, measurements);

	return measurements;
};

const listenToResize =
	(node: HTMLDivElement | null) => (callback: () => void) => {
		if (node == null) return () => {};

		const observer = new ResizeObserver((entries) => {
			if (entries.length === 0) return;
			const entry = entries[0];
			const { width, height } = entry.contentRect;
			if (width === 0 || height === 0) return;

			graphMeasurementsCache.delete(node);
			callback();
		});
		observer.observe(node);
		return () => {
			observer.unobserve(node);
		};
	};

export const CbsGraphMeasurementsProvider = ({
	children,
	canvasRef,
}: PropsWithChildren<{
	canvasRef: RefObject<HTMLDivElement | null>;
}>) => {
	const subscribeToResize = useCallback(
		(callback: () => void) => listenToResize(canvasRef.current)(callback),
		[canvasRef],
	);
	const measurements = useSyncExternalStore(
		subscribeToResize,
		() => getGraphMeasurements(canvasRef.current),
		() => defaultMeasurements,
	);

	return (
		<cbsGraphMeasurementsContext.Provider value={measurements}>
			{children}
		</cbsGraphMeasurementsContext.Provider>
	);
};

export const useCbsGraphMeasurements = () => {
	const cbsGraphContextValue = useContext(cbsGraphMeasurementsContext);

	if (cbsGraphContextValue == null)
		throw new Error('No CBS Graph context found');

	return cbsGraphContextValue;
};
